import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { EventsAPI } from '../api';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function monthLabel(year, month) {
  const d = new Date(year, month - 1, 1);
  try {
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return `${pad2(month)}.${year}`;
  }
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function toYmdLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatTimeShort(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Monday-based index: 0..6
function mondayIndex(date) {
  const js = date.getDay(); // 0 Sun .. 6 Sat
  return (js + 6) % 7;
}

export default function CommunityCalendar() {
  const { user } = useContext(UserContext);
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [events, setEvents] = useState([]);
  const [myBirthday, setMyBirthday] = useState('');
  const [myShowYear, setMyShowYear] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const dim = daysInMonth(year, month);
      const rangeStart = new Date(year, month - 1, 1, 0, 0, 0, 0);
      const rangeEnd = new Date(year, month - 1, dim, 23, 59, 59, 999);

      const [ccRes, evRes] = await Promise.all([
        api.get('/community-calendar/month', { params: { year, month } }),
        EventsAPI.list({
          from: rangeStart.toISOString(),
          to: rangeEnd.toISOString(),
          page: 1,
          pageSize: 100,
        }).catch((e) => ({ __error: e }))
      ]);

      const data = ccRes.data || {};
      const ccEvents = Array.isArray(data.events) ? data.events : [];

      let sysEvents = [];
      if (evRes && evRes.__error) {
        const e = evRes.__error;
        const msg = e?.response?.data?.error || e?.message;
        setError(msg ? `Events konnten nicht geladen werden: ${msg}` : 'Events konnten nicht geladen werden.');
      } else {
        const items = Array.isArray(evRes?.items) ? evRes.items : [];
        sysEvents = items
          .map((ev) => {
            const start = new Date(ev?.startAt);
            if (Number.isNaN(start.getTime())) return null;
            const date = toYmdLocal(start);
            if (!date) return null;
            return {
              kind: 'event',
              date,
              eventId: ev.id,
              title: ev.title || 'Event',
              startAt: ev.startAt,
              endAt: ev.endAt || null,
              locationType: ev.locationType || null,
              visibility: ev.visibility || null,
            };
          })
          .filter(Boolean);
      }

      const kindOrder = { event: 0, birthday: 1, anniversary: 2 };
      const merged = [...ccEvents, ...sysEvents].sort((a, b) => {
        const d = String(a.date).localeCompare(String(b.date));
        if (d !== 0) return d;
        const ak = kindOrder[String(a.kind)] ?? 99;
        const bk = kindOrder[String(b.kind)] ?? 99;
        if (ak !== bk) return ak - bk;
        if (a.kind === 'event' && b.kind === 'event') {
          return String(a.title || '').localeCompare(String(b.title || ''));
        }
        const an = String(a?.user?.displayName || '');
        const bn = String(b?.user?.displayName || '');
        return an.localeCompare(bn);
      });

      setEvents(merged);
      const my = data.my || {};
      setMyBirthday(typeof my.birthday === 'string' ? my.birthday : '');
      setMyShowYear(my.showBirthYear === true);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Kalender konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [year, month]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const ev of (Array.isArray(events) ? events : [])) {
      const key = String(ev?.date || '');
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return map;
  }, [events]);

  const grid = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const start = mondayIndex(first);
    const dim = daysInMonth(year, month);

    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return { start, dim, cells };
  }, [year, month]);

  async function saveBirthday() {
    setSaving(true);
    setError('');
    try {
      await api.post('/community-calendar/me/birthday', {
        birthday: (myBirthday || '').trim() || null,
        showBirthYear: !!myShowYear,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Konnte nicht speichern.');
    } finally {
      setSaving(false);
    }
  }

  function prevMonth() {
    let y = year;
    let m = month - 1;
    if (m < 1) { m = 12; y -= 1; }
    setYear(y);
    setMonth(m);
  }

  function nextMonth() {
    let y = year;
    let m = month + 1;
    if (m > 12) { m = 1; y += 1; }
    setYear(y);
    setMonth(m);
  }

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Community‑Kalender</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 920 }}>
          Hier siehst du Geburtstage (opt‑in), Mitglieds‑Jahrestage und Community‑Events.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {loading && <div style={{ opacity: 0.8 }}>Lade…</div>}
        {!loading && error && <div style={{ color: '#ff8484', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div style={{ ...card }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>{monthLabel(year, month)}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={prevMonth} style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}>◀</button>
                <button type="button" onClick={nextMonth} style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}>▶</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginTop: 12 }}>
              {['Mo','Di','Mi','Do','Fr','Sa','So'].map(w => (
                <div
                  key={w}
                  translate="no"
                  className="notranslate"
                  style={{ fontSize: 12, opacity: 0.7, textAlign: 'center', padding: '6px 0' }}
                >
                  {w}
                </div>
              ))}

              {grid.cells.map((d, idx) => {
                if (!d) {
                  return <div key={`e-${idx}`} style={{ minHeight: 96, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, background: 'rgba(0,0,0,0.12)' }} />;
                }
                const dateKey = `${year}-${pad2(month)}-${pad2(d)}`;
                const list = eventsByDate.get(dateKey) || [];
                const isToday = dateKey === todayKey;

                return (
                  <div
                    key={dateKey}
                    style={{
                      minHeight: 96,
                      border: isToday ? '1px solid rgba(50,205,137,0.75)' : '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 10,
                      background: isToday ? 'rgba(50,205,137,0.08)' : 'rgba(0,0,0,0.16)',
                      padding: 10,
                    }}
                  >
                    <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.95, marginBottom: 6, color: isToday ? '#32CD89' : undefined }}>
                      {d}
                    </div>
                    {list.length === 0 ? (
                      <div style={{ fontSize: 12, opacity: 0.45 }}>—</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 4 }}>
                        {list.map((ev, i) => {
                          const name = ev?.user?.displayName || 'Unbekannt';
                          if (ev.kind === 'event') {
                            const t = formatTimeShort(ev.startAt);
                            return (
                              <div key={`${dateKey}-e-${i}`} style={{ fontSize: 12, opacity: 0.92 }} title={ev.title || 'Event'}>
                                📅 {ev.title || 'Event'}{t ? <span style={{ opacity: 0.75 }}> · {t}</span> : null}
                              </div>
                            );
                          }
                          if (ev.kind === 'birthday') {
                            const age = ev.age != null ? ` (${ev.age})` : '';
                            return (
                              <div key={`${dateKey}-b-${i}`} style={{ fontSize: 12, opacity: 0.92 }}>
                                🎂 {name}{age}
                              </div>
                            );
                          }
                          if (ev.kind === 'anniversary') {
                            const years = typeof ev.years === 'number' ? ev.years : null;
                            const yearsTxt = years != null ? ` – ${years} Jahr${years === 1 ? '' : 'e'}` : '';
                            return (
                              <div key={`${dateKey}-a-${i}`} style={{ fontSize: 12, opacity: 0.92 }}>
                                ⭐ {name}{yearsTxt}
                              </div>
                            );
                          }
                          return (
                            <div key={`${dateKey}-x-${i}`} style={{ fontSize: 12, opacity: 0.92 }}>
                              {name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <div
                style={{
                  background: 'rgba(0,0,0,0.22)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 12,
                  maxWidth: 240,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 6, opacity: 0.9 }}>Legende</div>
                <div style={{ display: 'grid', gap: 4, opacity: 0.9 }}>
                  <div>📅 Event</div>
                  <div>🎂 Geburtstag</div>
                  <div>⭐ Jahrestag</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...card }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>Dein Geburtstag (opt‑in)</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
              Trage hier dein Geburtsdatum ein, damit es im Community‑Kalender angezeigt wird.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 460 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>Geburtsdatum</div>
                <input
                  type="date"
                  value={myBirthday}
                  onChange={(e) => setMyBirthday(e.target.value)}
                  style={{ width:'100%', background:'#111', color:'#fff', border:'1px solid #333', borderRadius:10, padding:'12px 14px', fontSize:14 }}
                />
              </div>

              <label style={{ display:'flex', alignItems:'center', gap: 8, fontSize: 13, opacity: 0.9 }}>
                <input type="checkbox" checked={!!myShowYear} onChange={(e) => setMyShowYear(e.target.checked)} />
                Geburtsjahr anzeigen (Alter im Kalender)
              </label>

              <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap' }}>
                <button
                  type="button"
                  onClick={saveBirthday}
                  disabled={saving}
                  className="hero-btn-primary"
                  style={{ textDecoration:'none', borderRadius: 10, padding:'10px 14px' }}
                >
                  {saving ? 'Speichere…' : 'Speichern'}
                </button>

                <button
                  type="button"
                  onClick={() => { setMyBirthday(''); setMyShowYear(false); }}
                  disabled={saving}
                  style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'10px 14px', borderRadius:10, cursor:'pointer' }}
                >
                  Leeren
                </button>
              </div>

              <div style={{ fontSize: 12, opacity: 0.65 }}>
                Hinweis: Die Geburtstags‑E‑Mail geht nur an dich selbst.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
